import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GeoPoint } from '@angular/fire/firestore';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import Uppy from '@uppy/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  MembershipStatus,
  MemberType,
} from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { AuthService } from '../../../../services/auth.service';
import * as memberActions from '../../../../state/actions/member.actions';
import { AppState } from '../../../../state/appstate';
import * as memberSelector from '../../../../state/selectors/member.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { ContactComponent } from '../../../components/dropdowns/contact/contact.component';
import { CountryComponent } from '../../../components/dropdowns/country/country.component';
import { GenderComponent } from '../../../components/dropdowns/gender/gender.component';
import { AddLocationComponent } from '../../../components/maps/add-location/add-location.component';
@Component({
  selector: 'app-create-member',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    GenderComponent,
    CountryComponent,
    ContactComponent,
    BtnSubmitComponent,
    BtnBackComponent,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    AddLocationComponent,
  ],
  templateUrl: './create-member.component.html',
  styleUrls: ['./create-member.component.css'],
})
export class CreateMemberComponent implements OnInit, OnDestroy {
  private readonly storage: Storage = inject(Storage);

  user: UserModel | null = null;
  userSub: Subscription | undefined;

  progressSub: Subscription | undefined;
  working: boolean = false;

  message: string = '';
  error: string | null = null;

  today: Date = new Date();

  groupId: string | null = null;

  pageForm: FormGroup;

  uuid = uuidv4();

  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    showSelectedFiles: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private toastr: ToastrService,
    private _activatedRoute: ActivatedRoute,
  ) {
    this.groupId = this._activatedRoute.snapshot.paramMap.get('id');

    this.pageForm = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      profession: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      contact: new FormControl({}),
      email: new FormControl('', [Validators.email]),
      dob: new FormControl(),
      gender: new FormControl('unspecified'),
      buildingNo: new FormControl('', [
        Validators.minLength(1),
        Validators.maxLength(10),
      ]),
      buildingName: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      addressLine1: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      addressLine2: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      city: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
      ]),
      country: new FormControl('', [Validators.required]),
      position: new FormControl(''),
    });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.progressSub?.unsubscribe();
  }

  ngOnInit(): void {
    this.userSub = this.store
      .select((state) => state.user.user)
      .subscribe((user) => {
        this.user = user;
      });

    this.progressSub = this.store
      .select((state) => state.members)
      .pipe(select(memberSelector.inProgress))
      .subscribe((inProgress) => {
        this.working = inProgress;
      });
  }

  setLocation(location: GeoPoint | null) {
    this.pageForm.patchValue({ location });
  }

  create() {
    this.error = null;

    if (this.pageForm.invalid) return;

    if (this.groupId == null) {
      this.error = 'Group Identifier missing.';
      return;
    }

    if (this.user == null) {
      this.error = 'User not found. Kindly login again.';
      return;
    }

    const file: any = this.uppy.getFiles()[0];

    const uid = this.auth.getUid();

    if (file != null && uid != null) {
      this.working = true;

      const fileExtn = file.name.split('.').pop();

      const storageRef = ref(
        this.storage,
        `unregistered/${this.groupId}/${this.uuid}.${fileExtn}`,
      );

      const metadata = {
        contentType: file.type,
        customMetadata: {
          uid: uid!,
          groupId: this.groupId,
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file.data, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.message = `${progress.toFixed(2)}%`;
        },
        (error) => {
          this.toastr.error(
            'Member image could not be updated. Please try again later.',
            'Image upload Failed',
          );
          this.working = false;
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.createDatabaseEntry(downloadURL);
          });
        },
      );
    } else {
      // Update member without Image
      this.createDatabaseEntry();
    }
  }

  createDatabaseEntry(downloadURL?: string) {
    var temp: any = {
      name: this.pageForm.get('name')?.value,
      unregistered: true, // Very important flag. Identifies if member is registered or not.
      gender: this.pageForm.get('gender')?.value ?? 'unspecified',
      email: this.pageForm.get('email')?.value,
      address: {
        buildingNo: this.pageForm.get('buildingNo')?.value,
        buildingName: this.pageForm.get('buildingName')?.value,
        addressLine1: this.pageForm.get('addressLine1')?.value,
        addressLine2: this.pageForm.get('addressLine2')?.value,
        city: this.pageForm.get('city')?.value,
        country: this.pageForm.get('country')?.value,
      },
    };

    if (this.pageForm.get('profession')?.value) {
      temp.profession = this.pageForm.get('profession')?.value;
    }

    if (this.pageForm.get('dob')?.value != null) {
      temp.dob = moment(this.pageForm.get('dob')?.value).toDate();
    }

    if (this.pageForm.get('contact')?.value.contact) {
      temp.contact = this.pageForm.get('contact')?.value;
    }

    if (this.pageForm.get('position')?.value) {
      temp.location = {
        id: `${this.groupId}_${this.uuid}`,
        name: this.pageForm.get('name')?.value,
        position: this.pageForm.get('position')?.value,
      };
    }

    if (downloadURL) {
      temp.photoURL = downloadURL;
    }

    temp.groupId = this.groupId;
    temp.memberType = MemberType.member;
    temp.memberId = this.uuid;
    temp.status = MembershipStatus.active;
    temp.created = new Date();
    temp.modified = temp.created;
    temp.createdById = this.user?.uid;
    temp.approvedById = this.user?.uid;

    this.store.dispatch(memberActions.create({ member: temp }));
  }
}
